import { Injectable } from '@angular/core';
import { ContextService } from '@core/services/core/context.service';
import { Device } from '@ionic-native/device/ngx';
import { FeatureFlags } from '@models/mmobile';
import { TranslateService } from '@ngx-translate/core';
import { Environment, MMobileService } from '../../../core/services/common';
import { LogService } from './log.service';

export interface DefaultLangs {
  EN: string;
  ES: string;
  CA: string;
  DEFAULT: string;
}

@Injectable({
  providedIn: 'root'
})
export class MMobileHelperService {
  private readonly LANG = '!#LANGUAGE!#';
  private readonly REMOTE_MMOBILE_TIMEOUT = 20000;

  envConfig: any;

  private mmobileReady = false;
  private isMmobileReadyPromise;
  private isMmobileReadyPromiseResolver;

  constructor(
    private mmobile: MMobileService,
    private trans: TranslateService,
    private device: Device,
    private log: LogService,
    private env: Environment,
    private ctx: ContextService
  ) {}

  async isReady() {
    await this.env.ready();
    await this.isMmobileReady();
  }

  // Unlike Environment Api, Mmobile current version doesnt support ready Promise,
  // hence this implementation
  notifyMmobileIsReady() {
    this.buildIsMmobileReadyPromise();

    if (!this.isMmobileReadyPromise.done) {
      this.isMmobileReadyPromiseResolver();
      this.mmobileReady = true;
    }
  }

  async getServiceUrlSafe(service: string) {
    await this.isReady();
    return this.getServiceUrl(service);
  }

  getServiceUrl(service: string) {
    return this.mmobile.getServiceUrl(service) as string;
  }

  getCommonServiceUrl(service: string, prefix?: string, version?: string) {
    return this.mmobile.getCommonServiceUrl(service, prefix, version) as string;
  }

  private isMmobileReady() {
    if (this.mmobileReady) {
      return Promise.resolve();
    }
    this.buildIsMmobileReadyPromise();
    return this.isMmobileReadyPromise as Promise<void>;
  }

  private buildIsMmobileReadyPromise() {
    if (this.isMmobileReadyPromise) {
      return;
    }
    this.isMmobileReadyPromise = new Promise(resolve => {
      this.isMmobileReadyPromiseResolver = resolve;
    }).then(() => {
      this.isMmobileReadyPromise.done = true;
    });
  }

  async initMMobile() {
    this.envConfig = Environment.config();
    this.envConfig.mmobileVersion = this.getVersion(this.envConfig);

    await this.mmobile
      .init(
        this.envConfig.mmobileUrl,
        this.envConfig.mmobileAppName,
        this.envConfig.mmobileVersion,
        null,
        this.REMOTE_MMOBILE_TIMEOUT
      )
      .catch(err => this.log.e(this, 'On init mmobile', JSON.parse(err)));
  }

  getUserDataUrl() {
    return `${this.envConfig.mmobileUrl}/services/j/${this.envConfig.mmobileAppName}/${this.envConfig.mmobileVersion}`;
  }

  getJWTOIMLoginUrl() {
    return `${this.getLoginBase()}/${this.envConfig.mmobileJwtOIMConfigName}`;
  }

  getJWTOIMLoginRefreshUrl() {
    return `${this.getJwtRefreshBase()}/${this.envConfig.mmobileJwtOIMConfigName}`;
  }

  getJWTOIMSimLoginUrl() {
    return `${this.getLoginBase()}/${
      this.envConfig.mmobileJwtOIMSimConfigName
    }`;
  }

  getJWTOIMAciLoginUrl() {
    return `${this.getLoginBase()}/${
      this.envConfig.mmobileJwtOIMAciConfigName
    }`;
  }

  getJWTTeCuidamosLoginUrl() {
    return `${this.getLoginBase()}/${
      this.envConfig.mmobileJwtTeCuidamosConfigName
    }`;
  }

  getJWTTeCuidamosSimLoginUrl() {
    return `${this.getLoginBase()}/${
      this.envConfig.mmobileJwtTeCuidamosSimConfigName
    }`;
  }

  isAppBlocked() {
    if (!this.ctx.isApp) {
      return false;
    }
    const deviceInfo = {
      version: this.device.version,
      model: this.device.model,
      platform: this.device.platform
    };

    return !this.mmobile.isActive() || this.mmobile.isDeviceLocked(deviceInfo);
  }

  getRequirePinUrl() {
    return `${this.envConfig.mmobileUrl}/pin`;
  }

  getValidatePinUrl() {
    return `${this.envConfig.mmobileUrl}/pin/token`;
  }

  getAppKey() {
    return this.envConfig.mmobileAppName as string;
  }

  getAppVersionKey() {
    return this.envConfig.version as string;
  }

  checkIsFeatureEnabled(feature: FeatureFlags | string) {
    const features = this.mmobile.getFeatures();
    if (!!features) {
      const key = this.ctx.isApp ? `${feature}_APP` : this.getWebFeatureKey(feature, features);
      return Object.keys(features).includes(key) ? !!features[key] : !!features[feature];
    }
    return false;
  }

  getAppOrPwa(...args: string[]) {
    const platform = this.ctx.isApp ? '_APP' : '_PWA';
    args[args.length - 1] = args[args.length - 1] + platform;

    return this.getCustomConfigData(...args);
  }

  /**
   * @description Get data from MMobile Custom Config tree
   *
   * mmobileHelper.LANG parameters will resolve to available language codes
   *
   * @example: getCustomConfigData('PRODUCTS', 'TEXTS', mmobileHelper.LANG, 'AGRARIAN')
   *  => 'PRODUCTS.TEXTS.es-ES.AGRARIAN'
   */
  getCustomConfigData<T = any>(...args: string[]): T {
    const config = this.mmobile.getCustomConfig();
    if (config == null) {
      return;
    }

    if (!args || !args.length) {
      return config;
    }
    const data = args.reduce((prev, current) => {
      if (!prev) {
        return null;
      } else {
        // resolve language for null arguments
        if (current === this.LANG) {
          const lang = this.languageFitsBest(prev);
          if (!lang) {
            return null;
          }
          return prev[lang];
        } else {
          return prev[current];
        }
      }
    }, config);
    return data;
  }

  async getVersionIdSafe() {
    await this.isReady();
    return this.getVersionId();
  }

  getVersionId() {
    const { mmobileConfigVersion, versionEnv } = Environment.config();
    let version = mmobileConfigVersion;
    if (versionEnv) {
      version += '-' + versionEnv;
    }
    return version;
  }

  private getVersion(environmentConfig) {
    const { mmobileConfigVersion, versionEnv } = environmentConfig;
    let version = mmobileConfigVersion;
    if (versionEnv) {
      version += '-' + versionEnv;
    }
    return version;
  }

  private getLoginBase() {
    return `${this.envConfig.mmobileUrl}/jwt/login/${this.envConfig.mmobileAppName}/${this.envConfig.mmobileVersion}`;
  }

  private getJwtRefreshBase() {
    return `${this.envConfig.mmobileUrl}/jwt/refresh/${this.envConfig.mmobileAppName}/${this.envConfig.mmobileVersion}`;
  }

  private languageFitsBest(availableLangsList?: string[]): string {
    let defaultLangs: DefaultLangs;
    try {
      defaultLangs = this.mmobile.getCustomConfig().DEFAULT_LANGUAGES;
    } catch (e) {
      return 'es';
    }
    const selectedLang = this.trans.currentLang;
    if (defaultLangs[selectedLang.toUpperCase()] != null) {
      return selectedLang;
    } else {
      return defaultLangs.DEFAULT;
    }
  }

  private getWebFeatureKey(feature: FeatureFlags | string, features: { [key: string]: string }) {
    if (!this.ctx.isDesktopDevice && Object.keys(features).includes(`${feature}_RESPONSIVE`)) {
      return `${feature}_RESPONSIVE`;
    }
    return `${feature}_WEB`;
  }
}
